import { useEffect, useCallback } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

import { useTeamsList } from '@redux/selectors/teams';
import { useLazyGetAllTeamsQuery } from '@redux/queries/team';
import {
  TEXT_INPUT_LABELS,
  TEXT_INPUT_PLACEHOLDERS,
} from '@utils/constants/form';
import Select from '@components/fields/select';

type Props<T extends FieldValues> = {
  value?: number;
  handleChange: ({
    field,
    text,
    onChange,
  }: {
    field: keyof T;
    text: string | number;
    onChange: UseFormSetValue<T>;
  }) => void;
  setValue: UseFormSetValue<T>;
  errorText?: string;
};

function TeamsSelect<T extends FieldValues>({
  value,
  handleChange,
  setValue,
  errorText,
}: Props<T>) {
  const teams = useTeamsList();
  const [getAllTeams] = useLazyGetAllTeamsQuery();

  const loadTeams = useCallback(
    async ({ search, isRefresh }: { search: string; isRefresh?: boolean }) => {
      const result = await getAllTeams({ search, isRefresh });
      if (result.data) {
        return result.data.map((team) => ({
          value: team.id,
          label: team.name,
        }));
      }
      return [];
    },
    [getAllTeams],
  );

  useEffect(() => {
    if (teams.length === 0) {
      loadTeams({ search: '', isRefresh: true });
    }
  }, [teams, loadTeams]);

  return (
    <Select
      isAsync
      id='team'
      loadOptions={(inputValue) => loadTeams({ search: inputValue })}
      defaultOptions={teams.map((team) => ({
        value: team.id,
        label: team.name,
      }))}
      label={TEXT_INPUT_LABELS.TEAM}
      placeholder={TEXT_INPUT_PLACEHOLDERS.TEAM}
      value={
        value
          ? {
              value,
              label: teams.find((team) => team.id === value)?.name || '',
            }
          : undefined
      }
      onChange={(newValue) =>
        handleChange({
          field: 'team',
          text: (newValue as { value: number; label: string })?.value,
          onChange: setValue,
        })
      }
      errorText={errorText}
    />
  );
}

export default TeamsSelect;
